import { VideoPlayerContainer, VideoQuality, type VideoPlayer } from '@zoom/videosdk';
import type { MediaStream } from '../index-types';

/**
 * Zoom公式の例
 * https://developers.zoom.us/docs/video-sdk/web/video/#start-video
 *
 * MDNのPiPサンプル
 * https://github.com/mdn/dom-examples/blob/main/document-picture-in-picture/main.js
 *
 * Reactの例もあるが、一旦適用していない
 * https://hackernoon.com/how-to-document-picture-in-picture-in-react-with-typescript
 */
export let pipWindow: Window | undefined;

const reAttachVideos = async (
  mediaStream: MediaStream | null,
  videoPlayers: Record<string, VideoPlayer>
) => {
  for (const [userIdStr, playerEl] of Object.entries(videoPlayers)) {
    try {
      await mediaStream?.attachVideo(Number(userIdStr), VideoQuality.Video_720P, playerEl);
    } catch (_) {
      // ユーザーが映像を送信していない場合は無視
    }
  }
};

export const enterPictureInPicture = async (
  videoContainerWrapper: HTMLDivElement,
  message: string,
  mediaStream?: MediaStream | null,
  videoPlayers?: Record<string, VideoPlayer>
) => {
  if (window.document.pictureInPictureEnabled && 'documentPictureInPicture' in window) {
    try {
      pipWindow = await (window.documentPictureInPicture as any).requestWindow();
      // Insert a stub after the container has been transferred to the PipWindow.
      const placeholder = document.createElement('div');
      placeholder.setAttribute(
        'style',
        'color: white;display: flex;' +
          'height: 100%;' +
          'margin: auto;' +
          'text-align: center;' +
          'justify-content: center;' +
          'align-items: center;'
      );

      placeholder.textContent = message;
      placeholder.id = 'PiP-placeholder';
      const videoContainer = videoContainerWrapper.querySelector('video-player-container');
      if (videoContainer) {
        const style = document.createElement('style');
        style.textContent =
          'body {background: black;}' +
          'p {color: white; padding: 10px}' +
          'ul {padding: 0}' +
          '.video-player {height:150px}' +
          `
          .avatar {
            color: white;
          }
          `;
        pipWindow!.document.head.appendChild(style);

        videoContainer.setAttribute('style', 'display: grid;');
        // Move the video container into the PiP window
        videoContainer.parentNode?.insertBefore(placeholder, videoContainer);
        pipWindow!.document.body.appendChild(videoContainer);

        // DOM移動後にSDKの描画コンテキストが切れるため、各映像を再アタッチして黒画面を防ぐ
        if (mediaStream && videoPlayers) {
          await reAttachVideos(mediaStream, videoPlayers);
        }

        pipWindow!.addEventListener('pagehide', async () => {
          const v = pipWindow!.document.querySelector('video-player-container');
          if (!v) {
            return;
          }
          await leavePictureInPicture(v as VideoPlayerContainer, mediaStream, videoPlayers);
        });
      }
    } catch (error) {
      console.error('Failed to enter Picture-in-Picture mode:', error);
    }
  } else {
    console.warn('Picture-in-Picture is not supported by your browser.');
  }
};

export const leavePictureInPicture = async (
  videoContainer: VideoPlayerContainer,
  mediaStream?: MediaStream | null,
  videoPlayers?: Record<string, VideoPlayer>
) => {
  const placeholder = document.getElementById('PiP-placeholder');
  try {
    if (placeholder && videoContainer) {
      placeholder.parentNode?.replaceChild(videoContainer, placeholder);

      // メインドキュメントに戻した後も再アタッチが必要
      if (mediaStream && videoPlayers) {
        await reAttachVideos(mediaStream, videoPlayers);
      }

      pipWindow!.close();
      pipWindow = undefined;
    }
  } catch (error) {
    console.error('Failed to leave Picture-in-Picture mode:', error);
  }
};
